
import { useState, useEffect, useRef } from 'react';

interface DemoStep {
  id: number;
  title: string;
  subtitle: string;
  icon: string;
  duration: number;
}

const steps: DemoStep[] = [
  { id: 1, title: 'Upload Photos', subtitle: 'Snap & upload job site images', icon: 'ri-camera-line', duration: 3000 },
  { id: 2, title: 'AI Analysis', subtitle: 'Detects materials & scope', icon: 'ri-brain-line', duration: 3500 },
  { id: 3, title: 'Pricing Engine', subtitle: 'Real Bunnings costs + labor', icon: 'ri-price-tag-3-line', duration: 3000 },
  { id: 4, title: 'Client Details', subtitle: 'Auto-fill or enter new', icon: 'ri-user-settings-line', duration: 2500 },
  { id: 5, title: 'Invoice Ready', subtitle: 'PDF in seconds', icon: 'ri-file-text-line', duration: 2000 },
];

export default function LiveDemo() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [showResult, setShowResult] = useState(false);

  const sectionRef = useRef<HTMLElement>(null);
  // In the browser `setInterval` returns a number, so we type the ref accordingly.
  const timerRef = useRef<number | null>(null);

  /** Observe when the component scrolls into view */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  /** Start the full demo sequence */
  const startDemo = () => {
    setIsPlaying(true);
    setCurrentStep(0);
    setProgress(0);
    setShowResult(false);
  };

  /** Reset everything and clear any running timers */
  const resetDemo = () => {
    setIsPlaying(false);
    setCurrentStep(0);
    setProgress(0);
    setShowResult(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  /** Core demo logic – progress bar & step advancement */
  useEffect(() => {
    if (!isPlaying) return;

    const step = steps[currentStep];
    if (!step) {
      // No more steps – display the final result
      setShowResult(true);
      setIsPlaying(false);
      return;
    }

    setProgress(0);
    const increment = (100 * 50) / step.duration; // amount to add every 50 ms

    timerRef.current = window.setInterval(() => {
      setProgress(prev => {
        const next = prev + increment;
        if (next >= 100) {
          // Step finished
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          // Small pause before moving to the next step
          setTimeout(() => setCurrentStep(s => s + 1), 200);
          return 100;
        }
        return next;
      });
    }, 50);

    // Cleanup on step change / component unmount
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isPlaying, currentStep]);

  /** Render the preview area based on the current step */
  const renderPreview = () => {
    if (showResult) {
      return (
        <div className="flex items-center justify-center h-full animate-scaleIn">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden w-full max-w-sm mx-auto">
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <i className="ri-building-2-fill text-white text-lg" />
                  </div>
                  <div className="text-white">
                    <p className="font-bold">INVOICE</p>
                    <p className="text-white/80 text-xs">INV-2025-0847</p>
                  </div>
                </div>
                <div className="text-right text-white">
                  <p className="text-xs text-white/80">Total</p>
                  <p className="text-2xl font-bold">$4,238</p>
                </div>
              </div>
            </div>

            <div className="p-5 space-y-3">
              <div className="flex justify-between text-sm">
                <div>
                  <p className="text-slate-500 text-xs">Bill To</p>
                  <p className="font-semibold text-slate-900">John Anderson</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-500 text-xs">Due</p>
                  <p className="font-semibold text-amber-600 text-sm">Feb 15, 2025</p>
                </div>
              </div>

              <div className="border-t border-b border-slate-100 py-3 space-y-2">
                {[
                  { desc: 'HVAC Installation - Labor (12hrs)', amount: '$2,088' },
                  { desc: 'Materials (Bunnings)', amount: '$1,850' },
                  { desc: 'Electrical Connection', amount: '$300' },
                ].map((item, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-slate-600">{item.desc}</span>
                    <span className="font-medium text-slate-900">{item.amount}</span>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <button className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-100 rounded-lg text-slate-700 text-xs font-medium hover:bg-slate-200 transition-colors whitespace-nowrap">
                  <i className="ri-download-line" /> PDF
                </button>
                <button className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-gradient-to-r from-amber-500 to-orange-600 rounded-lg text-white text-xs font-medium hover:from-amber-600 hover:to-orange-700 transition-colors whitespace-nowrap">
                  <i className="ri-send-plane-line" /> Send
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    /** Pre‑defined previews for each step (0‑4) */
    const previews: Record<number, JSX.Element> = {
      0: (
        <div className="space-y-4 animate-fadeIn">
          <div className="border-2 border-dashed border-amber-400/40 rounded-2xl p-6 text-center bg-amber-500/5">
            <div className="w-14 h-14 bg-amber-500/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <i className="ri-upload-cloud-2-line text-amber-400 text-2xl" />
            </div>
            <p className="font-semibold text-white text-sm mb-1">Drop job photos here</p>
            <p className="text-xs text-slate-400">or click to browse</p>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3].map(i => (
              <div
                key={i}
                className="aspect-square rounded-xl overflow-hidden relative animate-scaleIn"
                style={{ animationDelay: `${i * 150}ms` }}
              >
                <img
                  src={`https://public.readdy.ai/ai/img_res/bf1ae3abd3b0ba51808d117fa0372dbb.jpg${i}&width=200&height=200&seq=demo-live-photo-${i}&orientation=squarish`}
                  alt={`Job ${i}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-1 right-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
                  <i className="ri-check-line text-white text-[10px]" />
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 text-xs text-amber-400 bg-amber-500/10 rounded-lg p-2.5">
            <i className="ri-check-double-line" />
            <span>3 photos uploaded • Ready for analysis</span>
          </div>
        </div>
      ),
      1: (
        <div className="space-y-4 animate-fadeIn">
          <div className="bg-slate-800 rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
                <i className="ri-brain-line text-amber-400 text-xl animate-pulse" />
              </div>
              <div>
                <p className="font-semibold text-white text-sm">AI Analyzing...</p>
                <p className="text-xs text-slate-400">Processing 3 images</p>
              </div>
            </div>

            <div className="space-y-2.5">
              {['Detecting job type...', 'Identifying materials...', 'Calculating scope...', 'Fetching prices...'].map((text, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2.5 animate-slideRight"
                  style={{ animationDelay: `${i * 400}ms` }}
                >
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${i <= 2 ? 'bg-amber-500' : 'bg-slate-600'}`}>
                    {i <= 2 ? <i className="ri-check-line text-white text-[8px]" /> : <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-pulse" />}
                  </div>
                  <span className={`text-xs ${i <= 2 ? 'text-white' : 'text-slate-500'}`}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="bg-slate-800 rounded-xl p-3">
              <p className="text-[10px] text-slate-500 mb-0.5">Category</p>
              <p className="font-semibold text-white text-sm flex items-center gap-1.5">
                <i className="ri-temp-cold-line text-amber-400" /> HVAC
              </p>
            </div>
            <div className="bg-slate-800 rounded-xl p-3">
              <p className="text-[10px] text-slate-500 mb-0.5">Complexity</p>
              <p className="font-semibold text-white text-sm flex items-center gap-1.5">
                <i className="ri-bar-chart-line text-orange-400" /> Moderate
              </p>
            </div>
          </div>
        </div>
      ),
      2: (
        <div className="space-y-3 animate-fadeIn">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-800 rounded-2xl p-4 border-2 border-amber-500 relative">
              <div className="absolute -top-2 -right-2 bg-amber-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">Selected</div>
              <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center mb-2">
                <i className="ri-star-line text-amber-400 text-lg" />
              </div>
              <p className="font-bold text-white text-sm">Market Rate</p>
              <p className="text-[10px] text-slate-400 mb-2">Top tradie pricing</p>
              <p className="text-xl font-bold text-amber-400">$4,238</p>
            </div>

            <div className="bg-slate-800 rounded-2xl p-4 border border-white/10 opacity-60">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center mb-2">
                <i className="ri-wallet-3-line text-slate-400 text-lg" />
              </div>
              <p className="font-bold text-white text-sm">Budget</p>
              <p className="text-[10px] text-slate-400 mb-2">Fit client limit</p>
              <p className="text-xl font-bold text-slate-300">$3,000</p>
            </div>
          </div>

          <div className="bg-slate-800 rounded-xl p-3 space-y-2">
            {[
              { label: 'Labor (12 hrs @ $174/hr)', value: '$2,088' },
              { label: 'Materials (Bunnings)', value: '$1,850' },
              { label: 'Extras', value: '$300' },
            ].map((row, i) => (
              <div
                key={i}
                className="flex justify-between text-xs animate-slideRight"
                style={{ animationDelay: `${i * 200}ms` }}
              >
                <span className="text-slate-400">{row.label}</span>
                <span className="text-white font-semibold">{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      ),
      3: (
        <div className="space-y-3 animate-fadeIn">
          <div className="bg-slate-800 rounded-2xl p-4 space-y-3">
            {[
              { label: 'Client Name', value: 'John Anderson', icon: 'ri-user-line' },
              { label: 'Email', value: 'john@example.com', icon: 'ri-mail-line' },
              { label: 'Address', value: '42 Collins St, Melbourne', icon: 'ri-map-pin-line' },
            ].map((field, i) => (
              <div key={i} className="animate-slideRight" style={{ animationDelay: `${i * 200}ms` }}>
                <p className="text-[10px] text-slate-500 mb-1">{field.label}</p>
                <div className="flex items-center gap-2 p-2.5 bg-white/5 rounded-lg">
                  <i className={`${field.icon} text-slate-400 text-sm`} />
                  <span className="text-white text-sm">{field.value}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3 p-3 bg-amber-500/10 rounded-xl border border-amber-500/20">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-building-2-line text-white" />
            </div>
            <div>
              <p className="font-semibold text-white text-xs">Company Logo Added</p>
              <p className="text-[10px] text-slate-400">Acme Services Pty Ltd</p>
            </div>
            <i className="ri-check-circle-fill text-amber-400 ml-auto" />
          </div>
        </div>
      ),
      4: (
        <div className="flex items-center justify-center h-full animate-fadeIn">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
              <i className="ri-file-text-line text-white text-2xl" />
            </div>

            <div className="flex items-center justify-center gap-1.5 mb-3">
              {[0, 1, 2].map(i => (
                <div
                  key={i}
                  className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"
                  style={{ animationDelay: `${i * 200}ms` }}
                />
              ))}
            </div>

            <p className="font-semibold text-white">Generating Invoice...</p>
            <p className="text-xs text-slate-400 mt-1">Creating professional PDF</p>
          </div>
        </div>
      ),
    };

    return previews[currentStep] || null;
  };

  return (
    <section ref={sectionRef} id="live-demo" className="py-28 bg-slate-950 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
        <div className="absolute top-40 left-0 w-[500px] h-[500px] bg-amber-500/[0.03] rounded-full blur-[150px]" />
        <div className="absolute bottom-40 right-0 w-[400px] h-[400px] bg-orange-500/[0.03] rounded-full blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/15 mb-6">
            <i className="ri-play-circle-fill text-amber-400 text-sm" />
            <span className="text-amber-400 font-medium text-sm">Live Interactive Demo</span>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            See the Full Workflow in{' '}
            <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">Action</span>
          </h2>

          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Watch each step of the invoice creation process — from photo upload to professional PDF
          </p>
        </div>

        {/* Main content */}
        <div className={`flex flex-col lg:flex-row gap-8 items-start transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Step list & controls */}
          <div className="w-full lg:w-[38%] space-y-2">
            {steps.map((step, index) => (
              <button
                key={step.id}
                onClick={() => {
                  if (!isPlaying) {
                    setCurrentStep(index);
                    setShowResult(false);
                    setProgress(0);
                  }
                }}
                className={`w-full text-left p-4 rounded-2xl transition-all duration-400 cursor-pointer group ${
                  currentStep === index && !showResult
                    ? 'bg-white/[0.06] border border-amber-500/20 shadow-lg shadow-amber-500/5'
                    : index < currentStep || showResult
                    ? 'bg-white/[0.02] border border-transparent'
                    : 'bg-transparent border border-transparent hover:bg-white/[0.03]'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                      currentStep === index && !showResult
                        ? 'bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg'
                        : index < currentStep || showResult
                        ? 'bg-amber-500/20'
                        : 'bg-white/[0.06] group-hover:bg-white/[0.1]'
                    }`}
                  >
                    {index < currentStep || showResult ? (
                      <i className="ri-check-line text-amber-400 text-lg" />
                    ) : (
                      <i className={`${step.icon} text-lg ${currentStep === index ? 'text-white' : 'text-slate-400 group-hover:text-amber-400'}`} />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-[10px] font-semibold uppercase tracking-wider ${currentStep === index && !showResult ? 'text-amber-400' : 'text-slate-500'}`}>
                        Step {step.id}
                      </span>
                    </div>

                    <h3 className={`font-semibold text-sm ${currentStep === index && !showResult ? 'text-white' : index < currentStep || showResult ? 'text-white' : 'text-slate-300'}`}>
                      {step.title}
                    </h3>

                    <p className={`text-xs ${currentStep === index && !showResult ? 'text-slate-400' : 'text-slate-500'}`}>
                      {step.subtitle}
                    </p>
                  </div>
                </div>

                {/* Progress bar for the active step */}
                {currentStep === index && !showResult && isPlaying && (
                  <div className="mt-3 ml-15">
                    <div className="h-0.5 bg-white/[0.06] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-100"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                )}
              </button>
            ))}

            {/* Play / Stop / Restart controls */}
            <div className="pt-4 flex gap-3">
              {!isPlaying && !showResult ? (
                <button
                  onClick={startDemo}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-700 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 hover:scale-105"
                >
                  <i className="ri-play-fill" /> Play Full Demo
                </button>
              ) : isPlaying ? (
                <button
                  onClick={resetDemo}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap border border-white/10"
                >
                  <i className="ri-stop-fill" /> Stop
                </button>
              ) : (
                <button
                  onClick={resetDemo}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap border border-white/10"
                >
                  <i className="ri-refresh-line" /> Restart
                </button>
              )}
            </div>
          </div>

          {/* Demo preview area */}
          <div className="w-full lg:w-[62%]">
            <div className="bg-slate-800 rounded-t-2xl p-3 shadow-2xl border border-white/5 border-b-0">
              <div className="bg-slate-700 rounded-lg px-4 py-2 flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                </div>
                <div className="flex-1 bg-slate-600 rounded px-3 py-1 text-xs text-slate-400 flex items-center gap-1.5">
                  <i className="ri-lock-line text-emerald-400 text-[10px]" />
                  buildinvoicepro.com/invoice
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-slate-900 to-slate-950 rounded-b-2xl p-8 min-h-[420px] border border-white/5 border-t-0 flex flex-col justify-center">
              {renderPreview()}
            </div>
          </div>
        </div>
      </div>

      {/* Inline keyframe definitions */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
        @keyframes slideRight { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: translateX(0); } }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
        .animate-scaleIn { animation: scaleIn 0.4s ease-out forwards; }
        .animate-slideRight { animation: slideRight 0.4s ease-out forwards; }
      `}</style>
    </section>
  );
}
